import moment, { Moment } from 'moment';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty } from 'underscore';
import Api from '../../../../api/Api';
import useAnalyticsStore from '../../../../store/analyticsStore';
import useDatasetStore from '../../../../store/datasetStore';
import { usePointStore } from '../../../../store/pointStore';
import { ADVANCED_ANALYTICS, IBestDataset, IPlotVariable } from '../../../../types/analytics';
import { IAdvancedAnalyticsApiPayload } from '../../../../types/chart';
import { MarkerItem } from '../../../../types/home';
import { IAnalytcisUserInput } from '../../../../types/IAnalyticsUserInput';
import { IEnrichedMetadata } from '../../../../types/metadata';
import { formatRangeEnd, formatRangePartToTime } from '../../../../utils/dateRange';

interface UsePersistenceParams {
  coordinates: number[];
  previewDatasetId: string;
  categoryDataset: IBestDataset[];
  dateRange: [moment.Moment, moment.Moment];
  enrichedMetadata: IEnrichedMetadata[];
  onUpdate: (payload: IAdvancedAnalyticsApiPayload) => void;
  persistenceImage: string;
  persistenceLinks: string[];
  updating: boolean;
  error: string;
  pointName: string;
  userInput: IAnalytcisUserInput;
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>;
  onDatasetChange: (item: string) => void;
  gridCellSize: string;
  waterDepth: string;
  point?: MarkerItem;
  globalDateRange: any[];
  setGlobalDateRange: (item: any) => void;
  isForAllAnalytics: boolean;
  setIsForAllAnalytics: (item: any) => void;
}

export const usePersistenceParams = (params: UsePersistenceParams) => {
  const {
    coordinates,
    previewDatasetId,
    categoryDataset,
    dateRange,
    enrichedMetadata,
    onUpdate,
    persistenceImage,
    pointName,
    userInput,
    updateUserInput,
    onDatasetChange,
    gridCellSize,
    waterDepth,
    globalDateRange,
    setGlobalDateRange,
    isForAllAnalytics,
    setIsForAllAnalytics,
  } = params;

  const setEnrichedMetadata = useAnalyticsStore((state) => state.setEnrichedMetadata);
  const datasetBriefs = useDatasetStore((state) => state.datasetBriefs);
  const fwePoints = usePointStore((state) => state.fwePoints);
  const selfTriggered = useRef(false);
  const defaultsAppliedForRef = useRef<string | null>(null);
  const dataset2RequestRef = useRef(0);

  const [variablesX, setVariablesX] = useState<IPlotVariable[]>([]);
  const [variablesY, setVariablesY] = useState<IPlotVariable[] | null>(
    userInput.Persistence.variablesY
  );
  const [variableX, setVariableX] = useState<IPlotVariable | null>(userInput.Persistence.variableX);
  const [variableY, setVariableY] = useState<IPlotVariable | null>(userInput.Persistence.variableY);
  const [dataset2, setDataset2] = useState(userInput.Persistence.datasetY);
  const [duration, setDuration] = useState(userInput.Persistence.duration);
  const [sign, setSign] = useState<string>(userInput.Persistence.sign);
  const [sign2, setSign2] = useState<string>(userInput.Persistence.sign2);
  const [percentile, setPercentile] = useState(userInput.Persistence.percentile);
  const [threshold, setThreshold] = useState(userInput.Persistence.threshold || '');
  const [threshold2, setThreshold2] = useState(userInput.Persistence.threshold2 || '');
  const [conditionalDSSelected, setConditionalDSSelected] = useState(
    userInput.Persistence.conditionalDSSelected
  );
  const [IsChangeDatasetConditional, setIsChangeDatasetConditional] = useState(false);
  const [startDate, setStartDate] = useState(
    userInput.Persistence.startDate || moment(dateRange[0])
  );
  const [endDate, setEndDate] = useState(userInput.Persistence.endDate || moment(dateRange[1]));

  const getPersistenceImage = useCallback(
    (
      datasetId: string,
      datasetId2: string | null,
      thresh: string,
      thresh2: string,
      indexX: IPlotVariable,
      indexY: IPlotVariable | null,
      orderId: string,
      orderId2: string,
      startDate: string,
      endDate: string
    ) => {
      const [longitude, latitude] = coordinates;
      if (indexX) {
        const payload: IAdvancedAnalyticsApiPayload = {
          PointName: pointName,
          OrderId: orderId,
          OriginalOrderId: orderId,
          Longitude: longitude,
          Latitude: latitude,
          StartDate: startDate,
          EndDate: endDate,
          Title: indexX.Name,
          AcronymX: indexX.ShortName ? indexX.ShortName : indexX.Name,
          ColumnIndexX: indexX.Index,
          IntervalCountX: 16,
          AnalyticType: ADVANCED_ANALYTICS.PERSISTENCE_WEATHERWINDOW,
          Thresholds: thresh,
          Duration: duration.toString(),
          DatasetId: datasetId,
          Percentile: percentile,
          Sign1: sign,
          OriginalDatasetId: datasetId,
          enableConditional: datasetId2 !== '',
          OrderId2: orderId2,
          DatasetId2: datasetId2 ?? '',
          AcronymY: indexY != null ? (indexY.ShortName ? indexY.ShortName : indexY.Name) : '',
          TitleY: indexY?.Name ?? '',
          ColumnIndexY: indexY?.Index ?? 0,
          Thresholds2: thresh2,
          Sign2: sign2,
          IntervalCountY: 16,
        };
        onUpdate(payload);
      }
    },
    [coordinates, onUpdate, duration, sign, sign2, percentile, pointName]
  );

  const onUpdatePersistence = useCallback(
    (dateRange?: any) => {
      if (variableX && previewDatasetId) {
        const task = enrichedMetadata.find(
          (task: IEnrichedMetadata) =>
            task.DatasetId === previewDatasetId && task.CoordinatesString === coordinates.join()
        );

        if (previewDatasetId && task) {
          let orderId2 = task.OrderId;
          if (previewDatasetId !== dataset2 && dataset2 != '') {
            const task2 = enrichedMetadata.find(
              (task: IEnrichedMetadata) =>
                task.DatasetId === dataset2 && task.CoordinatesString === coordinates.join()
            );
            if (task2) {
              orderId2 = task2.OrderId;
            }
          }
          getPersistenceImage(
            previewDatasetId,
            dataset2,
            threshold,
            threshold2,
            variableX,
            variableY,
            task.OrderId,
            orderId2,
            formatRangePartToTime(dateRange ? dateRange[0] : startDate),
            formatRangeEnd(dateRange ? dateRange[1] : endDate)
          );
        }
      }
    },
    [
      variableX,
      variableY,
      previewDatasetId,
      dataset2,
      threshold,
      threshold2,
      duration,
      sign,
      sign2,
      percentile,
      startDate,
      endDate,
      pointName,
    ]
  );

  const doVariableChange = useCallback(
    (active: number) => {
      const item = variablesX.find((a) => a.Index === active)!;
      setVariableX(item);
    },
    [variablesX]
  );

  const onChangeDuration = useCallback((value: number | string | null | undefined) => {
    const MAX_HOURS = 336;
    if (value !== undefined && value !== null && !isNaN(Number(value))) {
      const numValue = Number(value);
      if (!isNaN(numValue)) {
        if (numValue > MAX_HOURS) {
          setDuration(MAX_HOURS);
        } else {
          setDuration(numValue);
        }
      } else {
        setDuration(1);
      }
    }
  }, []);

  const doSignChange = useCallback((value: string) => {
    setSign(value);
  }, []);

  const doSign2Change = useCallback((value: string) => {
    setSign2(value);
  }, []);

  const doPercentileChange = useCallback((value: number) => {
    setPercentile(value);
  }, []);

  const doDataset2Change = useCallback(
    async (value: string) => {
      const requestId = ++dataset2RequestRef.current;

      if (value != '') {
        let metadata = enrichedMetadata.find(
          (md: IEnrichedMetadata) =>
            md.DatasetId === value && md.CoordinatesString === coordinates.join()
        );

        if (!metadata) {
          try {
            setIsChangeDatasetConditional(true);
            metadata = await Api.metadata.getMetadata(value);
            const createFullExtractionTasks = await Api.service.createFullExtractionTasks(
              [metadata!],
              coordinates,
              pointName,
              gridCellSize,
              waterDepth,
              datasetBriefs,
              fwePoints
            );
            const addEnrichedMetadata = enrichedMetadata.concat(createFullExtractionTasks);
            setEnrichedMetadata(addEnrichedMetadata);
          } catch (err) {
            console.log(err);
          } finally {
            if (requestId === dataset2RequestRef.current) {
              setIsChangeDatasetConditional(false);
            }
          }
        }

        if (!metadata || requestId !== dataset2RequestRef.current) return;

        const withoutTime = metadata.ItemsInfo.filter(
          (item: IPlotVariable) => item.Name !== 'Time'
        );
        setVariablesY(withoutTime);
        setVariableY(withoutTime.length > 0 ? withoutTime[0] : null);
        setConditionalDSSelected(true);
      } else {
        setVariablesY(null);
        setVariableY(null);
        setConditionalDSSelected(false);
      }
      setDataset2(value);
    },
    [
      enrichedMetadata,
      coordinates,
      pointName,
      gridCellSize,
      waterDepth,
      datasetBriefs,
      fwePoints,
      setEnrichedMetadata,
    ]
  );

  const threshhold1Isvalid = useCallback((threshold1: string) => {
    const numbers = threshold1.split(',');
    if (numbers.length > 0 && numbers.length < 7) {
      const validNumbers = numbers.filter(
        (value: string) => value !== undefined && !isNaN(Number(value))
      );
      if (numbers.length === validNumbers.length) {
        return true;
      }
    }
    return false;
  }, []);

  const onChangeThreshold = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (threshhold1Isvalid(e.target.value)) {
        setThreshold(e.target.value);
      }
    },
    [threshhold1Isvalid]
  );

  const onChangeThreshold2 = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value !== undefined && !isNaN(Number(value))) {
      setThreshold2(value);
    }
  }, []);

  const doStartDateChange = useCallback(
    (value: any) => {
      if (value) {
        setStartDate(value);
        if (isForAllAnalytics) {
          setGlobalDateRange([value, endDate]);
        }
      }
    },
    [endDate, isForAllAnalytics, setGlobalDateRange]
  );

  const doEndDateChange = useCallback(
    (value: any) => {
      if (value) {
        setEndDate(value);
        if (isForAllAnalytics) {
          setGlobalDateRange([startDate, value]);
        }
      }
    },
    [startDate, isForAllAnalytics, setGlobalDateRange]
  );

  const onSelectDataset = useCallback(
    (value: string) => {
      onDatasetChange(value);
    },
    [onDatasetChange]
  );

  const handleCheckboxChange = useCallback(() => {
    setGlobalDateRange([startDate, endDate]);
    setIsForAllAnalytics(!isForAllAnalytics);
    selfTriggered.current = true;
  }, [startDate, endDate, setGlobalDateRange, isForAllAnalytics, setIsForAllAnalytics]);

  const doVariableYChange = useCallback(
    (active: number) => {
      const item = variablesY?.find((a) => a.Index === active);
      if (item) {
        setVariableY(item);
      }
    },
    [variablesY]
  );

  const disabledStartDate = useCallback(
    (current: Moment) => {
      const isAfterEndDate = current && current > endDate;
      return isAfterEndDate ? isAfterEndDate : current.isBefore(dateRange[0], 'day');
    },
    [endDate, dateRange]
  );

  const disabledEndDate = useCallback(
    (current: Moment) => {
      const isBeforeStartDate = current && current < startDate;
      return isBeforeStartDate ? isBeforeStartDate : current.isAfter(dateRange[1], 'day');
    },
    [startDate, dateRange]
  );

  const categories = useMemo(() => {
    const label = categoryDataset.map((bd: IBestDataset) => bd.label);
    return Array.from(new Set(label));
  }, [categoryDataset]);

  useEffect(() => {
    updateUserInput({
      Persistence: {
        datasetX: previewDatasetId,
        datasetY: dataset2,
        variableX: variableX,
        variablesY: variablesY,
        variableY: variableY,
        sign: sign,
        sign2: sign2,
        percentile: percentile,
        duration: duration,
        threshold: threshold,
        threshold2: threshold2,
        startDate: startDate,
        endDate: endDate,
        conditionalDSSelected: conditionalDSSelected,
      },
    });
  }, [
    previewDatasetId,
    dataset2,
    variableX,
    variablesY,
    variableY,
    sign,
    sign2,
    percentile,
    duration,
    threshold,
    threshold2,
    startDate,
    endDate,
    conditionalDSSelected,
  ]);

  useEffect(() => {
    if (!dataset2) {
      setVariableY(null);
      setConditionalDSSelected(false);
    }
  }, [dataset2]);

  useEffect(() => {
    if (enrichedMetadata) {
      const task = enrichedMetadata.find(
        (task: IEnrichedMetadata) =>
          task.DatasetId === previewDatasetId && task.CoordinatesString === coordinates.join()
      );
      if (task) {
        const itemsInfos = task.ItemsInfo.filter(
          (itemInfo: IPlotVariable) => itemInfo.Name !== 'Time'
        );
        setVariablesX(itemsInfos);
        if (itemsInfos.length > 1) {
          const selectionKey = `${previewDatasetId}|${coordinates.join(',')}|${itemsInfos.length}`;
          if (
            !isEmpty(categoryDataset) &&
            !isEmpty(coordinates) &&
            defaultsAppliedForRef.current !== selectionKey
          ) {
            defaultsAppliedForRef.current = selectionKey;
            const restoredVariable = userInput.Persistence.variableX
              ? itemsInfos.find((item) => item.Index === userInput.Persistence.variableX?.Index)
              : undefined;
            const indexX = restoredVariable ?? itemsInfos[0];
            setVariableX(indexX);
            let initialDateRange = dateRange;
            if (isForAllAnalytics) {
              initialDateRange = globalDateRange as [moment.Moment, moment.Moment];
              setStartDate(initialDateRange[0]);
              setEndDate(initialDateRange[1]);
            } else if (userInput.Persistence.startDate && userInput.Persistence.endDate) {
              initialDateRange = [userInput.Persistence.startDate, userInput.Persistence.endDate];
              setStartDate(initialDateRange[0]);
              setEndDate(initialDateRange[1]);
            } else {
              setStartDate(moment(initialDateRange[0]));
              setEndDate(moment(initialDateRange[1]));
            }
            selfTriggered.current = true;
            let orderId2 = '';
            if (!isEmpty(dataset2)) {
              const task = enrichedMetadata.find(
                (task: IEnrichedMetadata) =>
                  task.DatasetId === dataset2 && task.CoordinatesString === coordinates.join()
              );
              if (task) {
                orderId2 = task.OrderId;
              }
            }

            getPersistenceImage(
              previewDatasetId,
              dataset2 ?? '',
              threshold ?? '',
              threshold2 ?? '',
              indexX,
              variableY,
              task.OrderId,
              orderId2,
              formatRangePartToTime(initialDateRange[0]),
              formatRangeEnd(initialDateRange[1])
            );
          }
        }
      }
    }
  }, [
    categoryDataset,
    enrichedMetadata,
    coordinates,
    previewDatasetId,
    dataset2,
    globalDateRange,
    isForAllAnalytics,
  ]);

  useEffect(() => {
    if (isForAllAnalytics && persistenceImage !== '') {
      if (selfTriggered.current) {
        selfTriggered.current = false;
        return;
      }
      if (globalDateRange[0] !== startDate || globalDateRange[1] !== endDate) {
        onUpdatePersistence(globalDateRange);
      }
      setStartDate(globalDateRange[0]);
      setEndDate(globalDateRange[1]);
    }
  }, [isForAllAnalytics, persistenceImage]);

  return {
    variablesX,
    variablesY,
    variableX,
    variableY,
    dataset2,
    duration,
    sign,
    sign2,
    percentile,
    threshold,
    threshold2,
    conditionalDSSelected,
    IsChangeDatasetConditional,
    startDate,
    endDate,
    categories,
    onUpdatePersistence,
    doVariableChange,
    doVariableYChange,
    onChangeDuration,
    doSignChange,
    doSign2Change,
    doPercentileChange,
    doDataset2Change,
    onChangeThreshold,
    onChangeThreshold2,
    doStartDateChange,
    doEndDateChange,
    onSelectDataset,
    handleCheckboxChange,
    disabledStartDate,
    disabledEndDate,
  };
};
